import { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { apiFetch } from "../../../../api";
import s from "./AiChatPopup.module.css";

/* ═══════════════════════════════════
   Per-story chat cache (in-memory only, lost on full page reload)

   storyId -> { messages: [{id, role, content}], sessionId: string|null }

   Cache is keyed by story so reopening the AI popup for the same story
   resumes the previous conversation. A special "__no_story__" bucket is
   used when the host didn't provide a storyId (shouldn't happen in the
   three current consumers, but guards against undefined keys).
   ═══════════════════════════════════ */

const _chatCache = new Map();
function getCacheKey(storyId) {
  return storyId || "__no_story__";
}

function readCache(storyId) {
  return _chatCache.get(getCacheKey(storyId));
}

function writeCache(storyId, entry) {
  _chatCache.set(getCacheKey(storyId), entry);
}

/* ═══════════════════════════════════
   SVG Icons
   ═══════════════════════════════════ */

export function AiSparkleIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9.813 15.904 9 18.75l-.813-2.846a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 0 0 3.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 0 0-3.09 3.09ZM18.259 8.715 18 9.75l-.259-1.035a3.375 3.375 0 0 0-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 0 0 2.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 0 0 2.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 0 0-2.456 2.456ZM16.894 20.567 16.5 21.75l-.394-1.183a2.25 2.25 0 0 0-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 0 0 1.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 0 0 1.423 1.423l1.183.394-1.183.394a2.25 2.25 0 0 0-1.423 1.423Z" />
    </svg>
  );
}

function SendIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M3.478 2.404a.75.75 0 0 0-.926.941l2.432 7.905H13.5a.75.75 0 0 1 0 1.5H4.984l-2.432 7.905a.75.75 0 0 0 .926.94 60.519 60.519 0 0 0 18.445-8.986.75.75 0 0 0 0-1.218A60.517 60.517 0 0 0 3.478 2.404Z" />
    </svg>
  );
}

/* ═══════════════════════════════════
   Lightweight markdown renderer for AI replies

   Handles the subset the tutor model actually produces:
   - `**bold**` inline
   - Numbered lists (`1. `, `2. ` …)
   - Bullet lists (`- ` or `* `)
   - Paragraph breaks (blank lines)

   Anything else falls through as plain text with line breaks preserved.
   Deliberately tiny to avoid pulling in a full markdown dependency.
   ═══════════════════════════════════ */

// Inline: split a string on **...** and return an array of React nodes.
function renderInline(text, keyPrefix) {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith("**") && part.endsWith("**") && part.length > 4) {
      return <strong key={`${keyPrefix}-b-${i}`}>{part.slice(2, -2)}</strong>;
    }
    return part;
  });
}

const OL_ITEM_RE = /^(\d+)\.\s+(.*)$/;
const UL_ITEM_RE = /^[-*]\s+(.*)$/;

function MarkdownText({ text }) {
  if (!text) return null;

  // Split into blocks separated by blank lines.
  const blocks = String(text).split(/\n\s*\n/);

  return (
    <>
      {blocks.map((block, bi) => {
        const lines = block.split("\n").filter((l) => l.trim().length > 0);
        if (lines.length === 0) return null;

        const isOrdered = lines.every((l) => OL_ITEM_RE.test(l));
        const isBullet = !isOrdered && lines.every((l) => UL_ITEM_RE.test(l));

        if (isOrdered) {
          return (
            <ol key={bi}>
              {lines.map((l, li) => {
                const m = l.match(OL_ITEM_RE);
                return <li key={li}>{renderInline(m[2], `${bi}-${li}`)}</li>;
              })}
            </ol>
          );
        }

        if (isBullet) {
          return (
            <ul key={bi}>
              {lines.map((l, li) => {
                const m = l.match(UL_ITEM_RE);
                return <li key={li}>{renderInline(m[1], `${bi}-${li}`)}</li>;
              })}
            </ul>
          );
        }

        // Paragraph: preserve single-line breaks inside with <br/>.
        return (
          <p key={bi}>
            {lines.map((l, li) => (
              <span key={li}>
                {renderInline(l, `${bi}-${li}`)}
                {li < lines.length - 1 && <br />}
              </span>
            ))}
          </p>
        );
      })}
    </>
  );
}

/* ═══════════════════════════════════
   AI Chat Popup
   ═══════════════════════════════════ */

let _msgId = 0;

function getSuggestions(context) {
  if (context?.word?.word) {
    return ["More examples", "Explain the grammar", "Similar words"];
  }
  if (context?.sentence) {
    return ["Break it down", "Explain grammar", "Alternative ways"];
  }
  return ["Key vocabulary", "Grammar tips", "Cultural context"];
}

export default function AiChatPopup({ onClose, context, storyId }) {
  const cached = readCache(storyId);
  const [messages, setMessages] = useState(() => cached?.messages ?? []);
  const [sessionId, setSessionId] = useState(() => cached?.sessionId ?? null);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const abortRef = useRef(null);
  const contextRef = useRef(context);
  const panelRef = useRef(null);
  const overlayRef = useRef(null);
  const dragStartY = useRef(null);
  useEffect(() => { contextRef.current = context; }, [context]);

  // Persist any change to the per-story cache so reopening the popup resumes this conversation.
  useEffect(() => {
    writeCache(storyId, { messages, sessionId });
  }, [storyId, messages, sessionId]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      abortRef.current?.abort();
      _chatCache.delete(getCacheKey(storyId));
    };
  }, [onClose, storyId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  const sendMessage = useCallback(async (overrideText) => {
    const text = (overrideText || input).trim();
    if (!text || loading) return;

    if (!overrideText) setInput("");
    setMessages((prev) => [...prev, { id: ++_msgId, role: "user", content: text }]);
    setLoading(true);

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const res = await apiFetch("/api/v1/dashboard/ai-chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          message: text,
          session_id: sessionId,
          context: contextRef.current,
        }),
        signal: controller.signal,
      });

      if (!res.ok) throw new Error("Failed");
      const data = await res.json();
      setSessionId(data.session_id);
      setMessages((prev) => [...prev, { id: ++_msgId, role: "assistant", content: data.reply }]);
    } catch (e) {
      if (e.name === "AbortError") return;
      setMessages((prev) => [
        ...prev,
        { id: ++_msgId, role: "assistant", content: "Sorry, I couldn't respond. Please try again." },
      ]);
    } finally {
      setLoading(false);
    }
  }, [input, loading, sessionId]);

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        sendMessage();
      }
    },
    [sendMessage]
  );

  const handleInputChange = useCallback((e) => {
    setInput(e.target.value);
    e.target.style.height = "auto";
    e.target.style.height = Math.min(e.target.scrollHeight, 80) + "px";
  }, []);

  const handleClear = useCallback(() => {
    // Cancel any in-flight reply so its response doesn't repopulate the cleared chat.
    abortRef.current?.abort();
    setMessages([]);
    setSessionId(null);
    setLoading(false);
    // The cache-write effect will persist the cleared state automatically.
  }, []);

  // Swipe-up-to-close on mobile
  const handleTouchStart = useCallback((e) => {
    dragStartY.current = e.touches[0].clientY;
    if (panelRef.current) panelRef.current.style.transition = "none";
  }, []);

  const handleTouchMove = useCallback((e) => {
    if (dragStartY.current === null) return;
    const diff = e.touches[0].clientY - dragStartY.current;
    // Only allow dragging upward (negative diff) — throwing it to the top
    if (diff < 0 && panelRef.current) {
      panelRef.current.style.transform = `translateY(${diff}px)`;
    }
  }, []);

  const handleTouchEnd = useCallback((e) => {
    if (dragStartY.current === null) return;
    const diff = e.changedTouches[0].clientY - dragStartY.current;
    dragStartY.current = null;

    if (panelRef.current) {
      panelRef.current.style.transition = "transform 0.3s cubic-bezier(0.16, 1, 0.3, 1)";
      if (diff < -50) {
        // Swiped up enough — throw it to the top and close
        panelRef.current.style.transform = "translateY(-110%)";
        if (overlayRef.current) {
          overlayRef.current.style.transition = "opacity 0.3s ease 0.15s";
          overlayRef.current.style.opacity = "0";
        }
        setTimeout(onClose, 350);
      } else {
        // Snap back
        panelRef.current.style.transform = "translateY(0)";
      }
    }
  }, [onClose]);

  const suggestions = getSuggestions(context);
  const contextType = context?.word?.word ? "word" : context?.sentence ? "sentence" : "topic";

  return createPortal(
    <div className={s.chatOverlay} ref={overlayRef}>
      <div className={s.chatBackdrop} onClick={onClose} />
      <div className={s.chatPanel} ref={panelRef} onClick={(e) => e.stopPropagation()}>
        {/* Drag handle for mobile swipe-up-to-close */}
        <div
          className={s.dragHandle}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          <div className={s.dragHandleBar} />
        </div>
        {/* Header */}
        <div className={s.chatHeader}>
          <div className={s.chatHeaderIcon}>
            <AiSparkleIcon className={s.chatHeaderIconSvg} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h3 className={s.chatHeaderTitle}>AI</h3>
          </div>
          <button className={s.chatCloseBtn} onClick={onClose} title="Close" aria-label="Close AI chat">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <path d="M6 18 18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Messages */}
        <div className={s.chatMessages}>
          {messages.length === 0 && !loading && (
            <div className={s.chatEmpty}>
              <div className={s.chatEmptyIcon}>
                <AiSparkleIcon className={s.chatEmptyIconSvg} />
              </div>
              <p className={s.chatEmptyText}>
                Ask me anything about this {contextType}!
              </p>
              <p className={s.chatEmptyHint}>
                Your AI tutor is ready to help
              </p>
              <div className={s.chatSuggestions}>
                {suggestions.map((text) => (
                  <button
                    key={text}
                    className={s.chatSuggestionChip}
                    onClick={() => sendMessage(text)}
                  >
                    {text}
                  </button>
                ))}
              </div>
            </div>
          )}
          {messages.map((msg) => (
            <div
              key={msg.id}
              className={`${s.chatMsg} ${msg.role === "user" ? s.chatMsgUser : s.chatMsgAi}`}
            >
              <span className={s.chatMsgLabel}>
                {msg.role === "user" ? "You" : "AI Tutor"}
              </span>
              <div className={s.chatMsgBubble}>
                {msg.role === "assistant" ? (
                  <MarkdownText text={msg.content} />
                ) : (
                  msg.content
                )}
              </div>
            </div>
          ))}
          {loading && (
            <div className={s.chatTyping}>
              <span className={s.chatTypingDot} />
              <span className={s.chatTypingDot} />
              <span className={s.chatTypingDot} />
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Control strip — clear button, right above the input */}
        <div className={s.chatControls}>
          <div />
          <button
            className={s.clearBtn}
            onClick={handleClear}
            disabled={messages.length === 0 && !loading}
            title="Clear this conversation"
            aria-label="Clear conversation"
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 6h18" />
              <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
            </svg>
            <span>Clear</span>
          </button>
        </div>

        {/* Input */}
        <div className={s.chatInputArea}>
          <textarea
            ref={inputRef}
            className={s.chatInput}
            placeholder="Ask about this word, sentence, or story..."
            value={input}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            rows={1}
          />
          <button
            className={s.chatSendBtn}
            onClick={() => sendMessage()}
            disabled={!input.trim() || loading}
            title="Send"
          >
            <SendIcon className={s.chatSendIcon} />
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
